import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { filter } from "rxjs/operators";
import { ProfileMethod } from "src/app/profilers/ProfileMethod.decorator";
import { v4 as uuid } from "uuid";
import { ContextService } from "../context/state/context.service";
import { ModelVersion, ModelVersionData } from "../models/state/model";
import { UpdateModelVersionDataAsync } from "../models/state/model.actions";
import { ToastService } from "../shared/toast.service";
import {
  CategoryLookup,
  CategoryModelFolder,
  Expression,
  OperandType,
  OperatorType,
} from "./category-builder.models";
import { CategoryModelUtils } from "./category-model-utilities";
import { selectSandboxCategory } from "./state/working-category.selectors";

@Injectable({
  providedIn: "root",
})
export class CategoryModelService {
  workingVersion: ModelVersion;
  categoryLookup: CategoryLookup;
  sandbox: Expression;

  uuid = uuid;

  constructor(
    private contextService: ContextService,
    private store: Store,
    private toastService: ToastService,
  ) {
    this.contextService.categoryModelContext$
      .pipe(filter((context) => !!context))
      .subscribe((context) => {
        this.workingVersion = context.version;
      });

    this.contextService.categoryLookup$.subscribe(
      (lookup) => (this.categoryLookup = lookup),
    );

    this.store
      .select(selectSandboxCategory)
      .subscribe((s) => (this.sandbox = s));
  }

  addFolder(id: string, label: string) {
    const folder: CategoryModelFolder = {
      id,
      label,
      children: [],
    };

    const data = {
      ...this.workingVersion.version_data,
      category_tree: [
        ...this.workingVersion.version_data.category_tree,
        folder,
      ],
    };

    return { data };
  }

  removeFolder(folderID: string) {
    const data = {
      ...this.workingVersion.version_data,
      category_tree: this.workingVersion.version_data.category_tree.reduce(
        (folders: CategoryModelFolder[], folder: CategoryModelFolder) => {
          // Not the folder to delete; keep it
          if (folder.id !== folderID) {
            return [...folders, folder];
          }

          // Is folder to delete, check that it has no children
          if (folder.children?.length) {
            throw new FolderNotEmptyError();
          }

          // Delete the folder (by not adding it to the accumulator)
          return folders;
        },
        [],
      ),
    };

    return { data };
  }

  renameFolder(folderID: string, label: string) {
    const data = {
      ...this.workingVersion.version_data,
      category_tree: this.workingVersion.version_data.category_tree.map(
        (folder: CategoryModelFolder) => {
          if (folderID === folder.id) {
            return {
              ...folder,
              label: label,
            };
          } else {
            return folder;
          }
        },
      ),
    };

    return { data };
  }

  addCategory(category: Expression, folderID: string) {
    const data = {
      ...this.workingVersion.version_data,
      category_tree: this.workingVersion.version_data.category_tree.map(
        (folder: CategoryModelFolder) => {
          if (folder.id === folderID) {
            return {
              ...folder,
              children: folder.children
                ? [...folder.children, category]
                : [category],
            };
          }
          return folder;
        },
      ),
    };

    return { data };
  }

  removeCategory(categoryID: string) {
    const data = {
      ...this.workingVersion.version_data,
      category_tree: this.workingVersion.version_data.category_tree.map(
        (folder: CategoryModelFolder) => {
          if (folder.children.some((child) => child.id === categoryID)) {
            return {
              ...folder,
              children: folder.children.filter(
                (child) => child.id !== categoryID,
              ),
            };
          } else {
            return folder;
          }
        },
      ),
    };

    return { data };
  }

  renameCategory(categoryID: string, label: string) {
    const data = {
      ...this.workingVersion.version_data,
      category_tree: this.workingVersion.version_data.category_tree.map(
        (folder) => {
          return {
            ...folder,
            children: folder.children.map((category) => {
              if (category.id === categoryID) {
                return {
                  ...category,
                  label,
                };
              }
              return category;
            }),
          };
        },
      ),
    };

    return { data };
  }

  setVisibility(categoryID: string, hidden: boolean) {
    const data = {
      ...this.workingVersion.version_data,
      category_tree: this.workingVersion.version_data.category_tree.map(
        (folder) => {
          return {
            ...folder,
            children: folder.children.map((category) => {
              if (category.id === categoryID) {
                return {
                  ...category,
                  hidden,
                };
              }
              return category;
            }),
          };
        },
      ),
    };

    return { data };
  }

  updateCategory(category: Expression) {
    const data = {
      ...this.workingVersion.version_data,
      category_tree: this.workingVersion.version_data.category_tree.map(
        (folder) => {
          return {
            ...folder,
            children: folder.children.map((c) => {
              if (c.id === category.id) {
                return category;
              }
              return c;
            }),
          };
        },
      ),
    };

    return { data };
  }

  moveCategoryOrFolder(id: string, parentOrPeerID: string) {
    const treeWithOriginalNodeDeleted =
      this.workingVersion.version_data.category_tree.map(
        (folder: CategoryModelFolder) => {
          if (folder.children.some((child) => child.id === id)) {
            return {
              ...folder,
              children: folder.children.filter((child) => child.id !== id),
            };
          }
          return folder;
        },
      );

    const node = this.categoryLookup[id];
    const treeWithNewNode = treeWithOriginalNodeDeleted.map(
      (folder: CategoryModelFolder) => {
        const peerIndex = folder.children.findIndex(
          (child) => child.id === parentOrPeerID,
        );
        if (folder.id === parentOrPeerID) {
          return {
            ...folder,
            children: [node, ...folder.children],
          };
        } else if (peerIndex > -1) {
          const newChildren = [...folder.children];
          newChildren.splice(peerIndex + 1, 0, node);
          return {
            ...folder,
            children: newChildren,
          };
        } else {
          return folder;
        }
      },
    );

    const data = {
      ...this.workingVersion.version_data,
      category_tree: treeWithNewNode,
    };

    return { data };
  }

  findAllReferencesToCategory(categoryID: string, tree: CategoryModelFolder[]) {
    return tree.reduce(
      (referencedBy: string[], folder) => [
        ...referencedBy,
        ...folder.children
          .filter((c) => this.findAllCategoryReferences(c).includes(categoryID))
          .map((c) => c.id),
      ],
      [],
    );
  }

  findAllCategoryReferences(expression: Expression) {
    switch (expression.type) {
      case OperandType.CATEGORY_REFERENCE:
        return [expression.value];
      default:
        return (
          expression.children?.reduce(
            (catrefs: string[], child) => [
              ...catrefs,
              ...this.findAllCategoryReferences(child),
            ],
            [],
          ) || []
        );
    }
  }

  assertNoCircularReferences(
    expression: Expression,
    categories: CategoryLookup,
    visited: Set<string> = new Set(),
  ) {
    if (!expression) return visited;

    // Check for a circular dependency by checking if we've visited this expression before
    if (visited.has(expression.id)) {
      const cycle = [...Array.from(visited), expression.id]
        .map((id) => categories[id].label || `Unnamed Category(${id})`)
        .join(" -> ");
      throw new CircularReferenceError(cycle);
    }

    // Only add category ids to the visited set
    if (categories[expression.id]) {
      visited.add(expression.id);
    }

    // Now check children
    expression.children
      // Replace category references with their referenced categories
      ?.map((e) => {
        switch (e.type) {
          case OperandType.CATEGORY_REFERENCE:
            return categories[e.value];
          default:
            return e;
        }
      })
      .forEach((e) => {
        this.assertNoCircularReferences(e, categories, visited);
      });

    return visited.delete(expression.id);
  }

  assertNoOrphanedReferences(expression: Expression, lookup: CategoryLookup) {
    const orphans = [];
    this.findAllCategoryReferences(expression).forEach((id) => {
      if (!lookup[id]) {
        orphans.push(id);
      }
    });

    if (orphans.length) {
      throw new OrphanedReferencesError(
        `${expression.label} -> Unknown Categor${
          orphans.length > 1 ? "ies" : "y"
        } (${orphans.join(",")})`,
      );
    }
  }

  validateModel(category_tree: CategoryModelFolder[]) {
    // Get all the errors that exist on the model
    const errors = this._validateModel(category_tree);

    // Log each error to the console
    errors.forEach((e) => console.error(e));

    // Format all the errors into a single error message
    const message = errors
      .map((e) => `\t${e.name.toUpperCase()}: ${e.message}`)
      .join("\n");

    if (errors.length) {
      throw new InvalidCategoryModelError(message);
    }
  }

  checkForDuplicateNames(category_list: Expression[]) {
    if (!category_list) return [];

    const duplicateNames = new Set<string>();

    for (let folder of category_list) {
      const containsDuplicateLabel = category_list?.some(
        (c) =>
          c.label.toLowerCase() === folder.label.toLowerCase() &&
          c.id !== folder.id,
      );

      if (containsDuplicateLabel) duplicateNames.add(folder.label);
    }
    return [...duplicateNames];
  }

  checkForEmptyLogic(category_list: Expression[]) {
    if (!category_list) return [];

    const categoriesWithEmptyLogic = [];

    for (let category of category_list) {
      if (!category.children?.length)
        categoriesWithEmptyLogic.push(category.label);
    }

    return categoriesWithEmptyLogic;
  }

  getParentId(categoryId: string) {
    const parent = this.workingVersion.version_data.category_tree.find(
      (folder: CategoryModelFolder) =>
        folder.children.find(
          (category: Expression) => category.id === categoryId,
        ),
    );
    return parent?.id;
  }

  getParentOrPeerID(categoryTree: CategoryModelFolder[], categoryID: string) {
    for (const [idxOfFolder, folder] of categoryTree.entries()) {
      if (folder.id === categoryID) {
        if (idxOfFolder === 0) return undefined;
        const precedingFolder = categoryTree[idxOfFolder - 1];
        return precedingFolder?.children?.length
          ? precedingFolder.children[precedingFolder?.children?.length - 1].id
          : precedingFolder.id;
      } else if (folder?.children?.length) {
        const idxOfCategory = folder.children.findIndex(
          (category: Expression) => category.id === categoryID,
        );
        if (idxOfCategory === 0) {
          return folder.id;
        } else if (idxOfCategory > 0) {
          return folder.children[idxOfCategory - 1].id;
        }
      }
    }
  }

  validateBeforeUpdate(action: {
    version: ModelVersion;
    data: ModelVersionData;
  }) {
    try {
      // We want to validate the sandbox as part of any validation
      // This involves pretending it's part of the model
      const folder: CategoryModelFolder = {
        id: uuid(),
        label: "Fake Sandbox Folder",
        children: [this.sandbox],
      };

      // Validate the model tree, including sandbox
      const tree = [folder, ...action.data.category_tree];
      this.validateModel(tree);

      // Critically, we only persist the actual model data without the sandbox
      this.store.dispatch(
        new UpdateModelVersionDataAsync(action.version, action.data),
      );
    } catch (error) {
      // Catch the error and toast
      this.toastService.openErrorToast(`${error.name}\n${error.message}`);

      // Raise the error again
      throw new InvalidCategoryModelError(error.message);
    }
  }

  @ProfileMethod({ color: "green" })
  _validateModel(category_tree: CategoryModelFolder[]) {
    const list = CategoryModelUtils.getCategoryList(category_tree);
    const lookup = CategoryModelUtils.getCategoryLookup(category_tree);

    const errors: Error[] = [];
    list.forEach((expression) => {
      try {
        // Check for circular references
        this.assertNoCircularReferences(expression, lookup);

        // Check for orphaned references
        this.assertNoOrphanedReferences(expression, lookup);
      } catch (error) {
        errors.push(error);
      }
    });
    return errors;
  }

  static createParentFolderWithCatRefChildren(parent: CategoryModelFolder) {
    // L1 object that will be hardcoded as type: "or"
    const parentFolder = {
      id: parent.id,
      label: parent.label,
      type: OperatorType.OR,
      children: [],
    } as Expression;

    parent.children.forEach((category) => {
      // Turn the L2 into a category ref for the corresponding L1/parent's children array
      const categoryRef = {
        type: OperandType.CATEGORY_REFERENCE,
        value: category.id,
      } as Expression;

      parentFolder.children.push(categoryRef);
    });

    return { parentFolder };
  }
}

export class CircularReferenceError extends Error {
  name: string = "Circular Category Reference";
}

export class OrphanedReferencesError extends Error {
  name: string = "Orphaned Category Reference";
}

export class FolderNotEmptyError extends Error {
  name: string = "Folder Not Empty";
}

export class InvalidCategoryModelError extends Error {
  name: string = "Invalid Category Model";
}
